import type { Order } from '@/common/order.common'
import { getHoursPerUnit } from '@/helper/order.helper'
import { SERVICE_CODES, type SibServiceCode } from './useSibCapacityOverview'

/**
 * Year progress per service in Mode B, as decided in SIB-730 and illustrated by the "Jahresfortschritt"
 * demo:
 *
 *   target(t)   = expected cumulative units by day t according to the order schedules / ordered units
 *   progress(t) = completed cumulative units by day t / ordered units
 *
 * Each order is expected to be worked evenly over its own period (inclusive calendar days, the same
 * schedule SIB-827 uses), so the target curve steepens where orders overlap and stays flat where
 * none is running. Only the part of an order inside the year counts. Services are never added up:
 * TOJ units, consulting hours, training days and pilot units have no common unit.
 *
 * The projection continues from today at today's pace ratio (completed / expected), following the
 * remaining order schedule: projection(t) = completed(today) + pace x (expected(t) - expected(today)).
 * Nothing is capped, so work beyond the order shows above 100 %.
 */

export type ProgressActivity = {
  date: string
  type: string
  timeQuantity: number | null
  customerId?: number | null
}

export type ServiceProgress = {
  code: SibServiceCode
  /** Units ordered for the part of the orders inside the year. */
  ordered: number
  /** Cumulative percentages per day of the year (index 0 = 1 January). */
  target: number[]
  /** Up to and including today in the running year, the whole year for a past year. */
  completed: (number | null)[]
  /** From today to year end in the running year; null everywhere otherwise. */
  projection: (number | null)[]
  completedUnits: number
}

export type WorkProgress = {
  year: number
  days: number
  /** Index of today, or null when the year is not the running one. */
  todayIndex: number | null
  services: ServiceProgress[]
}

const DAY_MS = 86_400_000

/** Day index of a 'YYYY-MM-DD...' date within `year`; may be negative or beyond the year. */
function dayIndex(isoDate: string, year: number): number {
  const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number)
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(year, 0, 1)) / DAY_MS)
}

function normalizeCode(type: string): SibServiceCode | null {
  const code = type === 'TOJ_CUSTOMER' ? 'TOJ' : type
  return (SERVICE_CODES as readonly string[]).includes(code) ? (code as SibServiceCode) : null
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

export function buildWorkProgress(params: {
  year: number
  today: Date
  orders: Order[]
  activities: ProgressActivity[]
  /** Only this recipient's orders and activities; null for all. */
  recipientId: number | null
  hoursPerUnit?: Record<string, number>
}): WorkProgress {
  const { year, today, orders, activities, recipientId, hoursPerUnit = {} } = params
  const days = dayIndex(`${year + 1}-01-01`, year)
  const todayRaw = dayIndex(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`,
    year
  )
  const todayIndex = todayRaw >= 0 && todayRaw < days ? todayRaw : null
  // The last day with actual data: today in the running year, the year end for a past year.
  const lastActual = todayIndex ?? (todayRaw >= days ? days - 1 : -1)
  const forRecipient = (customerId: number | null | undefined) =>
    recipientId == null || Number(customerId ?? 0) === recipientId

  const services = SERVICE_CODES.map(code => {
    const schedules: { start: number; length: number; units: number }[] = []
    for (const order of orders) {
      if (!forRecipient(order.customerId)) continue
      const hours = (order.buckets ?? [])
        .filter(b => normalizeCode(b.serviceType) === code)
        .reduce((sum, b) => sum + (b.plannedHours ?? 0), 0)
      if (hours <= 0) continue
      const start = dayIndex(order.startTime, year)
      const end = dayIndex(order.endTime, year)
      if (end < start) continue
      schedules.push({
        start,
        length: end - start + 1,
        units: hours / getHoursPerUnit(code, hoursPerUnit[code]),
      })
    }
    // Expected units by the end of day d, counting only work scheduled inside the year.
    const expectedBy = (d: number) =>
      schedules.reduce(
        (sum, o) =>
          sum +
          o.units * (clamp01((d - o.start + 1) / o.length) - clamp01((0 - o.start) / o.length)),
        0
      )
    const ordered = expectedBy(days - 1)

    const daily = new Array<number>(days).fill(0)
    for (const a of activities) {
      if (normalizeCode(a.type) !== code || !forRecipient(a.customerId)) continue
      const d = dayIndex(a.date, year)
      if (d >= 0 && d < days) daily[d] += a.timeQuantity ?? 0
    }
    const cumulative: number[] = []
    daily.reduce((sum, v, i) => (cumulative[i] = sum + v), 0)

    const pct = (units: number) => (ordered > 0 ? (units / ordered) * 100 : 0)
    const target: number[] = []
    const completed: (number | null)[] = []
    const projection: (number | null)[] = []
    const expectedToday = todayIndex != null ? expectedBy(todayIndex) : 0
    const completedToday = todayIndex != null ? cumulative[todayIndex] : 0
    const pace = expectedToday > 0 ? completedToday / expectedToday : 1

    for (let d = 0; d < days; d++) {
      const expected = expectedBy(d)
      target.push(pct(expected))
      completed.push(d <= lastActual ? pct(cumulative[d]) : null)
      projection.push(
        todayIndex != null && d >= todayIndex
          ? pct(completedToday + pace * (expected - expectedToday))
          : null
      )
    }

    return {
      code,
      ordered,
      target,
      completed,
      projection,
      completedUnits: lastActual >= 0 ? cumulative[lastActual] : 0,
    }
  })

  return { year, days, todayIndex, services }
}
